// The matched-set record list for the Focused / Interrogate panel: a compact
// date—subject list where each row opens that record's source. Reused for the
// matched set, the active group, and the drill-down subset.
//
// Clicking a row opens the source IN PLACE (the drawer citations use) rather
// than navigating to the Records tab — navigating would unmount the whole
// Focused session (matched set, groups, pinned conversation). The caller wires
// `onNavigateToRecord` to that in-place opener; this component just renders the
// per-record open controls and reports which was clicked.
//
// Authored with React.createElement (not JSX), mirroring BackToSummaryBar, so
// the per-record open control's presence + click wiring are directly
// unit-testable under the repo's `node --test` runner (which strips TS types
// but not JSX). The visible control — not internal state — is what gets tested.

import { createElement } from "react";
import type { FocusedRecord } from "../api";

interface Props {
  records: FocusedRecord[];
  onNavigateToRecord?: (recordId: string) => void;
}

export default function FocusedRecordList({
  records,
  onNavigateToRecord,
}: Props) {
  return createElement(
    "ul",
    { className: "focused-pinbar-list" },
    records.map((r) =>
      createElement(
        "li",
        { key: r.record_id },
        createElement(
          "button",
          {
            type: "button",
            className: "focused-pinbar-record",
            onClick: () => onNavigateToRecord?.(r.record_id),
            title: "Open this record",
          },
          createElement("span", { className: "focused-pinbar-date" }, r.date || "—"),
          // Prefer the deterministic content label; keep the original subject
          // as the tooltip (it may carry a claim number / sender).
          createElement(
            "span",
            {
              className: "focused-pinbar-subject",
              title: r.content_label ? r.subject || undefined : undefined,
            },
            r.content_label || r.subject || "(no subject)",
          ),
          r.collapse_count > 1
            ? createElement(
                "span",
                { className: "focused-pinbar-thread" },
                `thread of ${r.collapse_count}`,
              )
            : null,
        ),
      ),
    ),
  );
}
